import React, { CSSProperties, forwardRef } from "react";
import { SnackbarKey, useSnackbar } from "notistack";
import { wscTheme } from "../../theme/wscTheme";
import IconButton from "../IconButton/IconButton";
import { Icon } from "../Icon";
import Button from "../Button";
import {
  ActionsContainer,
  AlertIcon,
  AlertTitle,
  AssetName,
  Message,
  MessageText,
  StyledAlert,
  StyledSnackbarContent,
} from "./ToastMessage.styled";

export interface ToastMessageProps {
  id: SnackbarKey;
  severity: "error" | "info" | "success";
  message: string;
  title?: string;
  actionButton?: {
    text: string;
    onClick: VoidFunction;
  };
  hideCloseIcon?: boolean;
  hideStatusIcon?: boolean;
  assetName?: string;
  alignment?: CSSProperties["justifyContent"];
  allowTextOverflow?: boolean;
}

const ToastMessage = forwardRef<HTMLDivElement, ToastMessageProps>(
  (
    {
      id,
      title,
      severity,
      message,
      actionButton,
      hideCloseIcon = false,
      hideStatusIcon = false,
      assetName,
      alignment,
      allowTextOverflow = false,
    },
    ref,
  ) => {
    const { closeSnackbar } = useSnackbar();

    const handleClose = () => closeSnackbar(id);

    const assetNameToDisplay =
      assetName && assetName?.length > 24
        ? assetName?.substring(0, 24) + "..."
        : assetName;

    const getIcon = (name: string, color: any) => {
      return (
        <AlertIcon>
          <Icon name={name} size="big" color={color} isFill />
        </AlertIcon>
      );
    };

    const action = (actionButton || !hideCloseIcon) && (
      <ActionsContainer>
        {actionButton && (
          <Button
            onClick={() => {
              actionButton.onClick();
              handleClose();
            }}
            buttonType="secondary"
            buttonVariant="ghost"
            label={actionButton.text}
          />
        )}
        {!hideCloseIcon && (
          <IconButton
            size="big"
            name="close"
            //TODO: Consult with designers what should be the IconButton type here and replace
            type="UNSAFE_transparent"
            onClick={handleClose}
            title="Close"
          />
        )}
      </ActionsContainer>
    );

    const iconProps = hideStatusIcon
      ? { icon: false }
      : {
          iconMapping: {
            success: getIcon("check_circle", wscTheme.palette.success[100]),
            error: getIcon("warning", wscTheme.palette.warning[100]),
            info: getIcon("info", wscTheme.palette.neutral[0]),
          },
        };

    const hasMessage = Boolean(message?.trim() || assetName?.trim());

    return (
      <StyledSnackbarContent ref={ref} alignment={alignment}>
        <StyledAlert
          severity={severity}
          variant="standard"
          action={action}
          {...iconProps}
        >
          {title && <AlertTitle>{title}</AlertTitle>}
          {hasMessage && (
            <Message>
              {assetName && <AssetName>"{assetNameToDisplay}"</AssetName>}
              <MessageText allowTextOverflow={allowTextOverflow}>
                {message}
              </MessageText>
            </Message>
          )}
        </StyledAlert>
      </StyledSnackbarContent>
    );
  },
);

export default ToastMessage;
